// ── Game creation (host) ──

import { ref, set } from 'firebase/database';
import { getFirebaseDb, getCurrentUid } from '../firebase';
import { createShuffledBag } from '../game/bag';
import { createAnchors } from '../board/geometry';
import type { GameState, PlayerColor } from '../game/state';

/** Generate a 4-character room code */
function generateRoomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no ambiguous chars
  let code = '';
  for (let i = 0; i < 4; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

/** Generate a unique game ID */
function generateGameId(): string {
  return `game_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Create a new game and return the game ID and room code */
export async function createGame(
  playerName: string,
  playerColor: PlayerColor = 'black',
): Promise<{ gameId: string; roomCode: string }> {
  const db = getFirebaseDb();
  const uid = getCurrentUid();
  if (!uid) throw new Error('Not authenticated');

  const gameId = generateGameId();
  const roomCode = generateRoomCode();
  const bag = createShuffledBag();
  const anchors = createAnchors();

  const initialState: GameState = {
    meta: {
      created: Date.now(),
      status: 'waiting',
      player1: {
        uid,
        name: playerName,
        color: playerColor,
      },
      player2: null,
      roomCode,
      pieRuleAvailable: true,
    },
    bag: {
      sequence: bag,
      drawIndex: 0,
    },
    board: {
      vertices: anchors,
      strands: {},
    },
    turn: {
      number: 1,
      activePlayer: 'player1',
      phase: 'chooseTension',
      drawnTokens: null,
      chosenTension: null,
      chosenK: null,
      placedNode: null,
      drawnStrandTargets: [],
    },
    lacunae: {},
    scores: { black: 0, red: 0 },
    history: [],
  };

  // Write game state and room code index atomically
  await set(ref(db, `games/${gameId}`), initialState);
  await set(ref(db, `roomCodes/${roomCode}`), gameId);

  return { gameId, roomCode };
}
